import { concat, fn, get, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import Component from '@glimmer/component';
import {
  PrismBadge,
  PrismCard,
  PrismIcon,
  PrismLoadingBar,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq, gt, lt, not } from 'ember-truth-helpers';
import { convertKilometersToMiles } from 'frontend-utils/utils/distance';
import GeneralButtonsOtbSelfServiceButton from 'lumen/components/general/buttons/otb-self-service-button';
import GeneralCountryFlag from 'lumen/components/general/country-flag';
import GeneralDemandLevel from 'lumen/components/general/demand-level';
import GeneralEmptyStateOtbBanner from 'lumen/components/general/empty-state-otb-banner';
import GeneralEventName from 'lumen/components/general/event-name';
import HotelOverviewDashboardTilesMarketTrendsEventsTooltip from 'lumen/components/hotel/overview-dashboard/tiles/market-trends/events-tooltip';
import add from 'lumen/helpers/add';
import dayjsFormat from 'lumen/helpers/dayjs-format';
import getNumberSignClass from 'lumen/helpers/get-number-sign-class';
import hasFeature from 'lumen/helpers/has-feature';
import highlightElement from 'lumen/modifiers/highlight-element';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import {
  MAX_LENGTH_KEY_DATE_NOTE,
  OD_API_DATA_KEYS,
  OD_KEY_DATES_SOURCES,
  OD_SELECTED_TILES_IDS,
} from 'lumen/utils/constants/overview-dashboard-constants';
import getEventDateRangeDisplay from 'lumen/utils/dates/event-date-range-display';

interface HotelOverviewDashboardTilesMarketTrendsEventsSignature {
  Args: {
    data: unknown;
    isAlertsRequestLoading: unknown;
    isLoading: unknown;
    onKeyDateStarClicked: unknown;
    onSelectDayForDayDrilldown: unknown;
    selectedRange: unknown;
    selectedRefDaysAgo: unknown;
    tileId: unknown;
  };
}

export default class HotelOverviewDashboardTilesMarketTrendsEvents extends Component<HotelOverviewDashboardTilesMarketTrendsEventsSignature> {
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare myotbSs: Services['myotb-ss'];
  @service declare odAccess: Services['od-access'];
  @service declare odSelections: Services['od-selections'];

  // @ts-expect-error: Incorrect type
  hasDemandAccess = this.odAccess.isTileVisible(
    OD_SELECTED_TILES_IDS.demand_level,
  );

  odEventsTableScrollableContainerId = `ca-kpi-tile-events-scrollable-container-${guidFor(this)}`;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get daysWithEvents() {
    let daysWithEvents = [];
    // @ts-expect-error: Incorrect type
    if (!this.args.isLoading && this.args.data.eventsData) {
      // Filter all days that actually have events to show
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      daysWithEvents = this.args.data.eventsData.filter(
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        (fromDateData) => fromDateData.kpis.events.length,
      );

      if (!isEmpty(daysWithEvents)) {
        // get date ids
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const datesWithEvents = daysWithEvents.map(
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          (event) => event.kpis.from_date,
        );

        if (this.hasDemandAccess) {
          // Check if it was requested per period or as selected day
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          if (this.args.data.marketDemandData.fromDates) {
            // Filter all demands that actually have events to show
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const demandWithEvents =
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              this.args.data.marketDemandData.fromDates.filter((demand) =>
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
                datesWithEvents.includes(demand.kpis.from_date),
              );

            // Merge demandWithEvents into daysWithEvents to get the demandLevel into the array that is used to show the data in the table
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, max-depth
            for (let i = 0; i < daysWithEvents.length; i++) {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              daysWithEvents[i].demandLevel = demandWithEvents[i]
                ? // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                  demandWithEvents[i].getDataValueFromKey(
                    this.odSelections,
                    OD_API_DATA_KEYS.mi_demand_level,
                  )
                : '';
            }
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const demandDataWithDaySelection =
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              this.args.data.marketDemandData.kpis;

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            daysWithEvents[0].demandLevel = demandDataWithDaySelection
              ? // @ts-expect-error: Incorrect type
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
                this.args.data.marketDemandData.getDataValueFromKey(
                  this.odSelections,
                  OD_API_DATA_KEYS.mi_demand_level,
                )
              : '';
          }
        }

        if (!this.args.isAlertsRequestLoading) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          daysWithEvents.forEach((day) => {
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            day.isKeyDate = this.args.data.keyDates?.some(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (keyDate) => keyDate.fromDate === day.fromDate,
            );
            // @ts-expect-error: Incorrect type
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            day.kpis.events.forEach((event) => {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              event.startAndEndDate = getEventDateRangeDisplay(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                event.start_date,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                event.end_date,
                this.dates,
              );

              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              if (event.distance) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                event.distanceShort = this.hotelAndMarketService
                  .useImperialSystem
                  ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                    `${Math.round(convertKilometersToMiles(event.distance))} mi`
                  : // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                    `${this.intl.formatNumber(event.distance, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
              } else {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                event.distanceShort = this.intl.t('general.missing');
              }

              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              event.visitorsFormatted = event.visitors
                ? // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
                  this.intl.formatNumber(event.visitors)
                : this.intl.t('general.missing');
            });

            // Makes sure that first the holidays are listed followed by the events sorted by the amount of visitors
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const amountOfHolidays = day.kpis.events.filter(
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              (event) => event.type === 'holiday',
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            ).length;
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            const sortedEvents = day.kpis.events
              .splice(
                amountOfHolidays,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                day.kpis.events.length - amountOfHolidays,
              )
              // @ts-expect-error: Incorrect type
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              .sort((a, b) => b.visitors - a.visitors);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            day.kpis.events.splice(amountOfHolidays, 0, ...sortedEvents);
          });
        }

        if (this.hasPickupColumn) {
          // @ts-expect-error: Incorrect type
          // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          return daysWithEvents.map((fromDateData) => {
            const result = this.getOtbDataForSpecificFromAndReferenceDate(
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              fromDateData.fromDate,
              // @ts-expect-error: Incorrect type
              this.args.selectedRefDaysAgo.id,
            );

            return {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              fromDateData,
              ...result,
            };
          });
        }

        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
        return daysWithEvents.map((fromDateData) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          return { fromDateData };
        });
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return daysWithEvents;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasPickupColumn() {
    // Check if the OTB tile is visible for this subscription or sub is eligible for OTB integration.
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.odAccess.showMyOtbDatapoints;
  }

  // This is the number of accepted events/holidays shown in the KPI
  // Here we do make sure that events spanning multiple days don't get counted multiple times (check first_occurrence property)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get numberOfAcceptedEvents() {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return this.daysWithEvents.reduce((total, day) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const numberOfFirstOccurenceEvents = day.fromDateData.kpis.events.reduce(
        // @ts-expect-error: Incorrect type
        (nbOfUniqueEvents, event) => {
          let eventNumber = 0;

          // Note: if day drilldown is active we can ignore the first_occurrence flag. We just want to show the number of holidays/events on that single day
          // @ts-expect-error: Incorrect type
          if (this.odSelections.isDayDrilldownActive) {
            eventNumber = 1;
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            eventNumber = event.first_occurrence ? 1 : 0;
          }

          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return nbOfUniqueEvents + eventNumber;
        },
        0,
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return total + numberOfFirstOccurenceEvents;
    }, 0);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get showNumberOfAcceptedEventsBadge() {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
    return !this.args.isLoading && this.daysWithEvents.length;
  }

  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getOtbDataForSpecificFromAndReferenceDate(fromDate, selectedRefDaysAgoId) {
    let otbPickupDiff = null;
    let formattedOtbPickup = this.intl.t('general.missing');

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    const otbDataFromDate = this.args.data.otbData?.fromDates.find(
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      (otbDataObject) => otbDataObject.fromDate === fromDate,
    );

    if (otbDataFromDate) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      otbPickupDiff = otbDataFromDate.getDataValueFromKey(
        this.odSelections,
        OD_API_DATA_KEYS.my_otb_percentage_pickup,
        selectedRefDaysAgoId,
      );

      if (otbPickupDiff !== null) {
        if (otbPickupDiff !== 0) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          formattedOtbPickup = this.intl.formatNumber(otbPickupDiff, {
            signDisplay: 'always',
            style: 'percent',
          });
        } else {
          formattedOtbPickup = this.intl.t('overview_dashboard.kpi.no_change');
        }
      }
    }

    return {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      pickupValue: otbPickupDiff,
      formattedPickupValue: formattedOtbPickup,
    };
  }

  @action
  // @ts-expect-error: Incorrect type
  onEventDateClicked(dayObject): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    this.args.onSelectDayForDayDrilldown(dayObject.fromDateData.fromDate);
  }

  @action
  // @ts-expect-error: Incorrect type
  onEventStarClicked(dayObject, e): void {
    // We don't want the star click to trigger the day drilldown, so we stop the event propagation here
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    e.stopPropagation();

    // For the key dates added via events, the name of the events are added as the note
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
    let note = dayObject.fromDateData.kpis.events
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
      .map((event) => event.title)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .join(' / ');

    // Truncate the default note in case it exceeds the max number of characters allowed
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (note.length > MAX_LENGTH_KEY_DATE_NOTE) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      note = `${note.substring(0, MAX_LENGTH_KEY_DATE_NOTE - 3)}...`;
    }

    // @ts-expect-error: Incorrect type
    this.args.onKeyDateStarClicked(
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      dayObject.fromDateData,
      note,
      OD_KEY_DATES_SOURCES.event,
    );
  }

  <template>
    {{! @glint-nocheck }}
    {{! template-lint-disable no-nested-interactive require-presentational-children }}
    <PrismCard
      @contentLayout="noPadding"
      class="prism-card--od-events"
      {{highlightElement id=@tileId isLoading=@isLoading}}
    >
      <:header as |Header|>
        <div class="flex items-center">
          <Header.title>
            {{t "event.header.my_events_holidays"}}
          </Header.title>
          {{#if this.showNumberOfAcceptedEventsBadge}}
            <div class="ml-200">
              <PrismBadge
                @label={{t
                  "overview_dashboard.events.accepted"
                  number=this.numberOfAcceptedEvents
                }}
              />
            </div>
          {{/if}}
        </div>
      </:header>
      <:headerAfter>
        <div class="flex flex-row items-center">
          {{#if this.daysWithEvents.length}}
            <LinkTo
              @query={{hash
                eventsFilter="suggested"
                month=(dayjsFormat @selectedRange.start format="YYYY-MM")
              }}
              @route="hotel.events"
              class="text-300--medium prism-link"
              data-event-id="hotel_overview-kpi_main_redirect-click"
              data-event-value={{@tileId}}
              target="_blank"
            >
              {{t "overview_dashboard.kpi.event.link"}}
            </LinkTo>
          {{/if}}

          {{#if
            (and
              (not this.myotbSs.isOtbSelfServiceUpsellBannerVisible)
              (hasFeature "otb.empty_placeholder")
            )
          }}
            <div class="ml-300">
              <GeneralButtonsOtbSelfServiceButton
                @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.OD_EVENTS}}
                @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.OVERVIEW_DASHBOARD}}
              />
            </div>
          {{/if}}
        </div>
      </:headerAfter>
      <:content>
        <div class="od-events-content">
          {{#if @isLoading}}
            <PrismLoadingBar />
          {{else}}

            {{#if this.daysWithEvents.length}}
              {{#if @isAlertsRequestLoading}}
                <PrismLoadingBar />
              {{/if}}
              <GeneralEmptyStateOtbBanner
                @bannerPosition="top"
                @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.OD_EVENTS}}
                @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.OVERVIEW_DASHBOARD}}
              />
              <div class="od-events-table-container">
                <table
                  class="default-table od-events-table
                    {{unless
                      this.hasPickupColumn
                      'od-events-table--no-pickup'
                    }}"
                >
                  <thead class="table-header-fixed">
                    <tr>
                      {{#if this.odSelections.isDayDrilldownActive}}
                        <td class="text-200--bold col-name">
                          {{t "general.name"}}
                        </td>
                        <td class="col-date-long text-200--bold">
                          {{t "overview.dates"}}
                        </td>
                        <td class="col-distance text-200--bold">
                          {{t "overview.distance_to_hotel"}}
                        </td>
                        <td class="col-visitors text-200--bold">
                          {{t "overview.visitors"}}
                        </td>
                      {{else}}
                        <td class="col-star-days"></td>
                        <td class="col-date text-200--bold">
                          {{t "general.date"}}
                        </td>
                        <td class="col-nb-events text-200--bold">
                          #
                        </td>
                        <td class="text-200--bold">
                          {{t "general.name"}}
                        </td>
                        {{#if this.hasDemandAccess}}
                          <td class="col-demand-level text-200--bold">
                            {{t
                              "market_insight.overview.market_demand_chart.wowchange"
                            }}
                          </td>

                        {{/if}}
                        {{#if this.hasPickupColumn}}
                          <td class="col-my-pickup text-200--bold">
                            {{t
                              "overview_dashboard.kpi.event.header.my_pickup_vs_ref"
                              refPeriod=@selectedRefDaysAgo.label
                            }}
                          </td>
                        {{/if}}
                      {{/if}}
                    </tr>
                  </thead>

                  <tbody>
                    {{#each this.daysWithEvents as |day|}}
                      {{#if this.odSelections.isDayDrilldownActive}}
                        {{#each day.fromDateData.kpis.events as |event|}}
                          <tr
                            role="button"
                            {{on "click" (fn this.onEventDateClicked day)}}
                          >
                            <td class="col-name">
                              <div class="flex items-center">
                                {{#if (eq event.type "holiday")}}
                                  <span class="mr-100">
                                    <GeneralCountryFlag
                                      @countryCode={{if
                                        (gt event.countries.length 1)
                                        "multiple"
                                        (get event.countries 0)
                                      }}
                                    />
                                  </span>
                                {{else}}
                                  <span class="mr-100">
                                    <PrismIcon @name="calendar-accepted" />
                                  </span>
                                {{/if}}
                                <GeneralEventName
                                  @event={{hash
                                    isCancelled=event.is_cancelled
                                    isUnconfirmed=event.is_unconfirmed
                                    name=event.title
                                  }}
                                />
                              </div>
                            </td>
                            <td class="col-date-long">
                              {{event.startAndEndDate}}
                            </td>
                            <td class="col-distance">
                              {{event.distanceShort}}
                            </td>
                            <td class="col-visitors">
                              {{event.visitorsFormatted}}
                            </td>
                          </tr>
                        {{/each}}
                      {{else}}
                        <tr
                          role="button"
                          {{on "click" (fn this.onEventDateClicked day)}}
                        >
                          <td class="col-star-days">
                            <button
                              class="button--unstyled"
                              type="button"
                              {{on "click" (fn this.onEventStarClicked day)}}
                            >
                              {{#if day.fromDateData.isKeyDate}}
                                <div class="icon-star--key-dates">
                                  <PrismIcon
                                    @color="inherit"
                                    @name="star-filled"
                                    @size="small"
                                  />
                                </div>
                              {{else}}
                                <PrismIcon @name="star-outline" @size="small" />
                              {{/if}}
                            </button>
                          </td>
                          <td class="col-date">
                            {{dayjsFormat
                              day.fromDateData.fromDate
                              format=this.dates.partialDateFormat.WEEKDAY_AND_MONTH_AND_DAY_SHORT
                            }}
                          </td>
                          <td class="col-nb-events">
                            {{day.fromDateData.kpis.events.length}}
                          </td>
                          <td>
                            <div class="flex items-center">
                              {{#each
                                day.fromDateData.kpis.events
                                as |event index|
                              }}
                                {{#if (eq event.type "holiday")}}
                                  <span class="mr-100">
                                    <GeneralCountryFlag
                                      @countryCode={{if
                                        (gt event.countries.length 1)
                                        "multiple"
                                        (get event.countries 0)
                                      }}
                                    />
                                  </span>
                                {{else}}
                                  <span class="mr-100"><PrismIcon
                                      @name="calendar-accepted"
                                      @size="small"
                                    /></span>
                                {{/if}}

                                <GeneralEventName
                                  @event={{hash
                                    isCancelled=event.is_cancelled
                                    isUnconfirmed=event.is_unconfirmed
                                    name=event.title
                                  }}
                                />

                                {{#if
                                  (lt
                                    index
                                    (add day.fromDateData.kpis.events.length -1)
                                  )
                                }}
                                  <span class="mr-100 ml-100">/</span>
                                {{/if}}
                              {{/each}}
                            </div>
                            <HotelOverviewDashboardTilesMarketTrendsEventsTooltip
                              @dateFormat={{this.dates.partialDateFormat.WEEKDAY_AND_DATE_LONG}}
                              @dayData={{day}}
                            />
                          </td>
                          {{#if this.hasDemandAccess}}
                            <td class="col-demand-level">
                              <GeneralDemandLevel
                                @demandLevel={{day.fromDateData.demandLevel}}
                                @size="small"
                              />
                            </td>
                          {{/if}}
                          {{#if this.hasPickupColumn}}
                            <td class="col-my-pickup">
                              <span
                                class="{{getNumberSignClass day.pickupValue}}
                                  {{if
                                    (hasFeature 'otb.empty_placeholder')
                                    'text-brand font-bold'
                                  }}"
                              >
                                {{day.formattedPickupValue}}
                              </span>
                            </td>
                          {{/if}}
                        </tr>
                      {{/if}}
                    {{/each}}
                  </tbody>
                </table>
              </div>
            {{else}}
              <div class="flex justify-center">
                <PrismMessage
                  @illustration="100x100_critical"
                  @title={{t "overview_dashboard.kpi.event.empty_state.title"}}
                  class="flex justify-center"
                >
                  <:link>
                    <LinkTo
                      @query={{hash
                        eventsFilter="suggested"
                        month=(dayjsFormat
                          @selectedRange.start format="YYYY-MM"
                        )
                      }}
                      @route="hotel.events"
                      class="prism-link text-300--medium"
                      data-event-id="hotel_overview-kpi_main_redirect-click"
                      data-event-value={{concat @tileId "_empty"}}
                      target="_blank"
                      {{! Added _empty here so that we can distinguish between the 2 redirect links in this component (empty state or data present) }}
                    >
                      {{t "overview_dashboard.kpi.event.link"}}
                    </LinkTo>
                  </:link>
                </PrismMessage>
              </div>
            {{/if}}

          {{/if}}
        </div>
      </:content>
    </PrismCard>
  </template>
}
